import React, { useState, useEffect, useCallback } from 'react'
import { X, Mic, Square, Play, Loader2, AlertCircle } from 'lucide-react'
import useVoiceRecording from '../hooks/useVoiceRecording'
import useSpeechRecognition from '../hooks/useSpeechRecognition'

/**
 * VoiceRecorderModal - Modal component for voice recognition input
 * Records audio, sends to backend for recognition, and allows user to apply extracted data to form
 */
const VoiceRecorderModal = ({
  isOpen,
  onClose,
  satsueiMemoCategoryId,
  onApplyData
}) => {
  // Recording state
  const {
    isRecording,
    audioUrl,
    error: recordingError,
    formattedTime,
    startRecording,
    stopRecording,
    cancelRecording,
    getAudioAsBase64
  } = useVoiceRecording()

  // Recognition state
  const {
    transcript,
    error: recognitionError,
    recognizeSpeech,
    clearResults
  } = useSpeechRecognition()

  // Local state
  const [step, setStep] = useState('idle') // 'idle' | 'recording' | 'recorded' | 'processing' | 'error'
  const [errorMessage, setErrorMessage] = useState(null)

  // Reset state when modal opens (only when isOpen changes to true)
  // Note: We intentionally exclude clearResults and cancelRecording from deps
  // to prevent the effect from running when those functions change due to state updates
  useEffect(() => {
    if (isOpen) {
      setStep('idle')
      setErrorMessage(null)
      clearResults()
      cancelRecording()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Handle start recording
  const handleStartRecording = useCallback(async () => {
    const success = await startRecording()
    if (success) {
      setStep('recording')
    }
    // If failed, error will be displayed from the recordingError state
  }, [startRecording])

  // Handle stop recording
  const handleStopRecording = useCallback(() => {
    stopRecording()
    setStep('recorded')
  }, [stopRecording])

  // Handle cancel / retry
  const handleCancel = useCallback(() => {
    cancelRecording()
    clearResults()
    setStep('idle')
    setErrorMessage(null)
  }, [cancelRecording, clearResults])

  // Handle process audio
  const handleProcessAudio = useCallback(async () => {
    try {
      setStep('processing')
      setErrorMessage(null)
      const audioBase64 = await getAudioAsBase64()
      const result = await recognizeSpeech(audioBase64, satsueiMemoCategoryId)
      
      // Check if any data was extracted
      if (result.extractedData && Object.keys(result.extractedData).length > 0) {
        // Success - directly apply data and close modal
        onApplyData(result.extractedData)
        onClose()
      } else {
        // No data extracted - show error with retry option
        setErrorMessage('データを抽出できませんでした。項目名や値が認識されなかった可能性があります。もう一度録音してください。')
        setStep('error')
      }
    } catch (err) {
      console.error('Recognition failed:', err)
      setErrorMessage(err.message || '音声認識に失敗しました。もう一度お試しください。')
      setStep('error')
    }
  }, [getAudioAsBase64, recognizeSpeech, satsueiMemoCategoryId, onApplyData, onClose])


  // Don't render if not open
  if (!isOpen) return null

  // Combined error from recording or recognition hooks
  const hookError = recordingError || recognitionError

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">音声入力</h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="閉じる"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Hook Error Display (recording/recognition errors) */}
          {hookError && step !== 'error' && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle size={18} />
              <span className="text-sm">{hookError}</span>
            </div>
          )}

          {/* Recording UI */}
          {(step === 'idle' || step === 'recording' || step === 'recorded') && (
            <div className="text-center">
              {/* Microphone Animation */}
              <div className="flex justify-center mb-6">
                <div className={`relative p-8 rounded-full ${
                  step === 'recording' 
                    ? 'bg-red-100 animate-pulse' 
                    : 'bg-gray-100'
                }`}>
                  <Mic size={48} className={
                    step === 'recording' ? 'text-red-600' : 'text-gray-400'
                  } />
                  {step === 'recording' && (
                    <div className="absolute inset-0 rounded-full border-4 border-red-400 animate-ping" />
                  )}
                </div>
              </div>

              {/* Timer */}
              {step === 'recording' && (
                <div className="text-2xl font-mono text-red-600 mb-4">
                  {formattedTime}
                </div>
              )}

              {/* Instructions */}
              <p className="text-gray-600 mb-6">
                {step === 'idle' && 'マイクボタンを押して音声入力を開始してください'}
                {step === 'recording' && '話し終わったら停止ボタンを押してください'}
                {step === 'recorded' && '音声が録音されました。認識を実行しますか？'}
              </p>

              {/* Audio Preview */}
              {step === 'recorded' && audioUrl && (
                <div className="mb-6">
                  <audio controls src={audioUrl} className="w-full" />
                </div>
              )}

              {/* Control Buttons */}
              <div className="flex justify-center gap-4">
                {step === 'idle' && (
                  <button
                    onClick={handleStartRecording}
                    className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <Mic size={20} />
                    録音開始
                  </button>
                )}

                {step === 'recording' && (
                  <button
                    onClick={handleStopRecording}
                    className="flex items-center gap-2 px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors"
                  >
                    <Square size={20} />
                    停止
                  </button>
                )}

                {step === 'recorded' && (
                  <>
                    <button
                      onClick={handleCancel}
                      className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      やり直す
                    </button>
                    <button
                      onClick={handleProcessAudio}
                      className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Play size={20} />
                      認識実行
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Processing UI */}
          {step === 'processing' && (
            <div className="text-center py-8">
              <Loader2 size={48} className="mx-auto mb-4 text-blue-600 animate-spin" />
              <p className="text-gray-600">音声を認識中...</p>
            </div>
          )}

          {/* Error UI - shown when recognition fails or no data extracted */}
          {step === 'error' && (
            <div className="text-center">
              {/* Error Icon */}
              <div className="flex justify-center mb-6">
                <div className="p-8 rounded-full bg-red-100">
                  <AlertCircle size={48} className="text-red-600" />
                </div>
              </div>

              {/* Error Message */}
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">
                  {errorMessage}
                </p>
              </div>

              {/* Transcript if available */}
              {transcript && (
                <div className="mb-6 text-left">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">認識されたテキスト</h3>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-gray-900 text-sm">{transcript}</p>
                  </div>
                </div>
              )}

              {/* Retry Button */}
              <div className="flex justify-center">
                <button
                  onClick={handleCancel}
                  className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Mic size={20} />
                  もう一度録音する
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default VoiceRecorderModal
